import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter,
    DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Key } from "@phosphor-icons/react";

/**
 * Self-service password change. Used both from the user dropdown menu ("change
 * password" voluntary) and forced-on-open when `user.force_password_reset ===
 * true` (temporary password after admin reset).
 *
 * On success we `refresh()` the AuthContext so `force_password_reset` clears
 * without a page reload.
 */
export default function ChangePasswordDialog({ open, onOpenChange, forced = false }) {
    const { user, refresh } = useAuth();
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [submitting, setSubmitting] = useState(false);

    // Reset the form every time the dialog opens.
    useEffect(() => {
        if (open) { setCurrent(""); setNext(""); setConfirm(""); }
    }, [open]);

    const nextOk = next.length >= 8;
    const matchOk = next === confirm;
    const canSubmit = current.length > 0 && nextOk && matchOk && !submitting;

    const onSubmit = async () => {
        if (!canSubmit) return;
        setSubmitting(true);
        try {
            await api.post("/users/me/change-password", {
                current_password: current,
                new_password: next,
            });
            toast.success("Contraseña actualizada");
            await refresh();
            onOpenChange(false);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo cambiar la contraseña");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(o) => { if (!forced) onOpenChange(o); }}
        >
            <DialogContent
                data-testid="change-password-dialog"
                onEscapeKeyDown={(e) => { if (forced) e.preventDefault(); }}
                onPointerDownOutside={(e) => { if (forced) e.preventDefault(); }}
                onInteractOutside={(e) => { if (forced) e.preventDefault(); }}
            >
                <DialogHeader>
                    <div className="flex items-center gap-2">
                        <div className="w-9 h-9 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center shrink-0">
                            <Key size={18} weight="bold" />
                        </div>
                        <div>
                            <DialogTitle>
                                {forced ? "Cambia tu contraseña temporal" : "Cambiar contraseña"}
                            </DialogTitle>
                            <DialogDescription>
                                {forced
                                    ? "Un administrador reseteó tu contraseña. Elige una nueva para poder seguir usando el Hub con normalidad."
                                    : `Cambia la contraseña de ${user?.email}. Al confirmar volverás a la app con la nueva.`}
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <div className="space-y-3">
                    <div className="space-y-1.5">
                        <Label>Contraseña actual (o la temporal recibida)</Label>
                        <Input
                            type="password"
                            value={current}
                            onChange={(e) => setCurrent(e.target.value)}
                            data-testid="change-pwd-current"
                            autoFocus
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label>Nueva contraseña (mínimo 8 caracteres)</Label>
                        <Input
                            type="password"
                            value={next}
                            onChange={(e) => setNext(e.target.value)}
                            data-testid="change-pwd-new"
                        />
                        {next && !nextOk && (
                            <p className="text-xs text-red-600">Debe tener al menos 8 caracteres.</p>
                        )}
                    </div>
                    <div className="space-y-1.5">
                        <Label>Repite la nueva contraseña</Label>
                        <Input
                            type="password"
                            value={confirm}
                            onChange={(e) => setConfirm(e.target.value)}
                            data-testid="change-pwd-confirm"
                        />
                        {confirm && !matchOk && (
                            <p className="text-xs text-red-600">No coincide con la anterior.</p>
                        )}
                    </div>
                </div>

                <DialogFooter>
                    {!forced && (
                        <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="change-pwd-cancel">
                            Cancelar
                        </Button>
                    )}
                    <Button
                        onClick={onSubmit}
                        disabled={!canSubmit}
                        className="bg-brand-600 hover:bg-brand-700"
                        data-testid="change-pwd-submit"
                    >
                        {submitting ? "Guardando…" : "Guardar contraseña"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
